// VideoCall.jsx - Swapped video positions (your video on left, remote on right)
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import io from "socket.io-client";
import config from "../../../../config";
import { Camera } from "@mediapipe/camera_utils";
import { FaceMesh } from "@mediapipe/face_mesh";
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaWindowMinimize, FaExpandAlt } from "react-icons/fa";
import { MdCallEnd } from "react-icons/md";
import { toast } from "../../../../components/common/Toast";

// Import the new CSS
import "./VideoCall.css";

const VideoCall = forwardRef(({ 
  roomId, 
  userId, 
  token, 
  meetingId, 
  user, 
  selectedUser, 
  user1Id, 
  user2Id, 
  onMeetingEnd,
  isMinimized = false,
  onToggleMinimize
}, ref) => {
  // Refs
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const peerRef = useRef();
  const localStreamRef = useRef();
  const socketRef = useRef();
  const [isConnected, setIsConnected] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [faceMeshInitialized, setFaceMeshInitialized] = useState(false);

  // Analytics refs
  const cameraMetricsHistory = useRef({
    eye_contact_duration: [],
    blink_rate: [],
    facial_tension: [],
    smile_authenticity: [],
    head_tilt_openness: [],
    lean_posture: [],
    micro_joy: [],
    micro_fear: [],
    micro_disgust: [],
    micro_surprise: []
  });

  const voiceMetricsHistory = useRef({
    pitch_variability: [],
    speech_rate: [],
    pauses: [],
    vocal_tremor: [],
    warmth_resonance: [],
    interrupt_patterns: []
  });

  const lastCameraMinuteRef = useRef(null);
  const lastVoiceMinuteRef = useRef(null);
  
  const currentCameraMetrics = useRef({
    eye_contact_duration: 0.5,
    blink_rate: 0,
    facial_tension: 0.5,
    smile_authenticity: 0.5,
    head_tilt_openness: 0.5,
    lean_posture: 0.5,
    micro_joy: 0.5,
    micro_fear: 0.1,
    micro_disgust: 0.05,
    micro_surprise: 0.2
  });

  const currentVoiceMetrics = useRef({
    pitch_variability: 0.5,
    speech_rate: 0,
    pauses: 0,
    vocal_tremor: 0.1,
    warmth_resonance: 0.5,
    interrupt_patterns: 0
  });

  // Refs for analytics
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastBlinkTimeRef = useRef(null);
  const blinkCountRef = useRef(0);
  const lastFrameTimeRef = useRef(Date.now());
  const audioContextRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const audioSourceRef = useRef(null);
  const lastSpeechTimeRef = useRef(Date.now());
  const speechCountRef = useRef(0);
  const pauseStartTimeRef = useRef(null);
  const pitchHistoryRef = useRef([]);
  const lastInterruptTimeRef = useRef(null);
  const collectionIntervalRef = useRef(null);
  const audioAnalysisFrameRef = useRef(null);
  const lastFaceDetectionLog = useRef(Date.now());

  const addDebugLog = (message) => console.log(message);

  const getCurrentMinuteTimestamp = () => Math.floor(Date.now() / 1000 / 60) * 60;
  
  const addCameraDataPoint = () => {
    const timestamp = getCurrentMinuteTimestamp();
    if (lastCameraMinuteRef.current === timestamp) return;
    lastCameraMinuteRef.current = timestamp;
    
    Object.keys(cameraMetricsHistory.current).forEach(key => {
      let value = currentCameraMetrics.current[key];
      if (typeof value !== 'number') value = 0.5;
      if (key === 'blink_rate') value = Math.min(30, Math.max(0, value));
      
      cameraMetricsHistory.current[key].push({
        ts: timestamp,
        value: value
      });
    });
  };
  
  const addVoiceDataPoint = () => {
    const timestamp = getCurrentMinuteTimestamp();
    if (lastVoiceMinuteRef.current === timestamp) return;
    lastVoiceMinuteRef.current = timestamp;
    
    Object.keys(voiceMetricsHistory.current).forEach(key => {
      let value = currentVoiceMetrics.current[key];
      if (typeof value !== 'number') value = key === 'interrupt_patterns' ? 0 : 0.5;
      
      voiceMetricsHistory.current[key].push({
        ts: timestamp,
        value: value
      });
    });
  };

  const processFaceAnalytics = (landmarks) => {
    const now = Date.now();
    
    currentCameraMetrics.current.eye_contact_duration = calculateEyeContact(landmarks);
    currentCameraMetrics.current.blink_rate = calculateBlinkRate(now);
    currentCameraMetrics.current.facial_tension = calculateFacialTension(landmarks);
    currentCameraMetrics.current.smile_authenticity = calculateSmileAuthenticity(landmarks);
    currentCameraMetrics.current.head_tilt_openness = calculateHeadTilt(landmarks);
    currentCameraMetrics.current.lean_posture = calculateLeanPosture(landmarks);
    
    const microExpressions = detectMicroExpressions(landmarks);
    currentCameraMetrics.current.micro_joy = microExpressions.joy;
    currentCameraMetrics.current.micro_fear = microExpressions.fear;
    currentCameraMetrics.current.micro_disgust = microExpressions.disgust;
    currentCameraMetrics.current.micro_surprise = microExpressions.surprise;
    
    detectBlink(landmarks);
    
    if (now - lastFaceDetectionLog.current > 10000) {
      addDebugLog(`✅ Face detected! Eye: ${currentCameraMetrics.current.eye_contact_duration.toFixed(3)}`);
      lastFaceDetectionLog.current = now;
    }
  };

  const calculateEyeContact = (landmarks) => {
    const leftEyeInner = landmarks[133];
    const rightEyeInner = landmarks[362];
    if (leftEyeInner && rightEyeInner) {
      const gazeScore = Math.abs(leftEyeInner.x - rightEyeInner.x);
      return Math.min(0.95, Math.max(0.3, 0.7 + gazeScore * 0.3));
    }
    return 0.5;
  };
  
  const calculateBlinkRate = (now) => {
    const timeElapsed = (now - lastFrameTimeRef.current) / 1000;
    if (timeElapsed >= 1) {
      const blinkRate = (blinkCountRef.current / timeElapsed) * 60;
      blinkCountRef.current = 0;
      lastFrameTimeRef.current = now;
      return Math.min(25, Math.max(8, blinkRate));
    }
    return 12;
  };

  const calculateFacialTension = (landmarks) => {
    const browLeft = landmarks[70];
    const browRight = landmarks[300];
    if (browLeft && browRight) {
      const tension = Math.abs(browLeft.y - browRight.y);
      return Math.min(0.9, Math.max(0.1, tension * 10));
    }
    return 0.5;
  };

  const calculateSmileAuthenticity = (landmarks) => {
    const mouthLeft = landmarks[61];
    const mouthRight = landmarks[291];
    const mouthTop = landmarks[13];
    const mouthBottom = landmarks[14];
    if (mouthLeft && mouthRight && mouthTop && mouthBottom) {
      const mouthWidth = Math.abs(mouthLeft.x - mouthRight.x);
      const mouthHeight = Math.abs(mouthTop.y - mouthBottom.y);
      const smileRatio = mouthHeight / (mouthWidth + 0.01);
      return Math.min(0.95, Math.max(0.3, smileRatio * 3));
    }
    return 0.5;
  };

  const calculateHeadTilt = (landmarks) => {
    const nose = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    if (nose && leftEye && rightEye) {
      const eyeCenterY = (leftEye.y + rightEye.y) / 2;
      const tilt = nose.y - eyeCenterY;
      return Math.min(0.9, Math.max(0.3, 0.6 + tilt * 2));
    }
    return 0.5;
  };

  const calculateLeanPosture = (landmarks) => {
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    if (leftShoulder && rightShoulder) {
      const shoulderWidth = Math.abs(leftShoulder.x - rightShoulder.x);
      const leanScore = 1 - Math.min(1, shoulderWidth);
      return Math.max(0.4, Math.min(0.9, leanScore + 0.4));
    }
    return 0.5;
  };

  const detectMicroExpressions = (landmarks) => {
    const mouthLeft = landmarks[61];
    const mouthRight = landmarks[291];
    let joy = 0.5;
    if (mouthLeft && mouthRight) {
      const mouthWidth = Math.abs(mouthLeft.x - mouthRight.x);
      joy = Math.min(0.95, Math.max(0.3, mouthWidth * 2));
    }
    
    const browLeft = landmarks[70];
    const browRight = landmarks[300];
    let fear = 0.1;
    if (browLeft && browRight) {
      fear = Math.min(0.9, Math.max(0.05, Math.abs(browLeft.y - browRight.y) * 5));
    }
    
    const nose = landmarks[1];
    const mouthTop = landmarks[13];
    let disgust = 0.05;
    if (nose && mouthTop) {
      disgust = Math.min(0.8, Math.max(0.03, Math.abs(nose.y - mouthTop.y) * 2));
    }
    
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const mouthBottom = landmarks[14];
    let surprise = 0.2;
    if (leftEye && rightEye && mouthTop && mouthBottom) {
      const eyeOpenness = (Math.abs(leftEye.y - landmarks[159].y) + Math.abs(rightEye.y - landmarks[386].y)) / 2;
      const mouthOpenness = Math.abs(mouthTop.y - mouthBottom.y);
      surprise = Math.min(0.95, Math.max(0.1, (eyeOpenness + mouthOpenness) * 5));
    }
    
    return { joy, fear, disgust, surprise };
  };

  const detectBlink = (landmarks) => {
    const now = Date.now();
    if (!lastBlinkTimeRef.current) {
      lastBlinkTimeRef.current = now;
      return;
    }
    
    const leftEyeTop = landmarks[159];
    const leftEyeBottom = landmarks[145];
    const rightEyeTop = landmarks[386];
    const rightEyeBottom = landmarks[374];
    
    if (leftEyeTop && leftEyeBottom && rightEyeTop && rightEyeBottom) {
      const leftEyeOpen = Math.abs(leftEyeTop.y - leftEyeBottom.y);
      const rightEyeOpen = Math.abs(rightEyeTop.y - rightEyeBottom.y);
      const isBlinking = (leftEyeOpen < 0.01 || rightEyeOpen < 0.01);
      
      if (isBlinking && (now - lastBlinkTimeRef.current) > 200) {
        blinkCountRef.current++;
        lastBlinkTimeRef.current = now;
      }
    }
  };

  // Stop FaceMesh and camera
  const stopFaceMesh = async () => {
    if (cameraRef.current) {
      try {
        await cameraRef.current.stop();
      } catch (err) {
        console.log(`Error stopping camera: ${err.message}`);
      }
      cameraRef.current = null;
    }
    
    if (faceMeshRef.current) {
      try {
        await faceMeshRef.current.close();
      } catch (err) {
        console.log(`Error closing FaceMesh: ${err.message}`);
      }
      faceMeshRef.current = null;
    }
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    setFaceMeshInitialized(false);
  };

  // Initialize FaceMesh (safe version with error handling)
  const initFaceMesh = async (forceVideoOff = null) => {
    const videoIsOff = forceVideoOff !== null ? forceVideoOff : isVideoOff;
    
    if (videoIsOff) {
      console.log("Video is off, skipping FaceMesh initialization");
      return;
    }
    
    if (faceMeshInitialized) {
      console.log("FaceMesh already initialized");
      return;
    }
    
    if (!localVideoRef.current || !localStreamRef.current) {
      console.log("Cannot initialize FaceMesh: video element or stream not ready");
      return;
    }
    
    console.log("Initializing MediaPipe FaceMesh...");
    
    try {
      await stopFaceMesh();
      
      faceMeshRef.current = new FaceMesh({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
        }
      });
      
      faceMeshRef.current.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });
      
      faceMeshRef.current.onResults((results) => {
        if (isVideoOff) return;
        
        if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0 && !isMinimized) {
          const landmarks = results.multiFaceLandmarks[0];
          processFaceAnalytics(landmarks);
        }
      });
      
      if (localVideoRef.current && localStreamRef.current && !isMinimized && !videoIsOff) {
        cameraRef.current = new Camera(localVideoRef.current, {
          onFrame: async () => {
            if (isVideoOff) return;
            
            if (faceMeshRef.current && localVideoRef.current && !isMinimized) {
              try {
                await faceMeshRef.current.send({image: localVideoRef.current});
              } catch (err) {
                // Silently fail
              }
            }
          },
          width: 640,
          height: 480
        });
        await cameraRef.current.start();
        console.log("Camera started successfully");
        setFaceMeshInitialized(true);
      }
    } catch (err) {
      console.log(`Error initializing FaceMesh: ${err.message}`);
      setFaceMeshInitialized(false);
    }
  };

  // Re-initialize FaceMesh when coming back from minimized state
  useEffect(() => {
    if (!isMinimized && localStreamRef.current && localVideoRef.current && !faceMeshInitialized) {
      setTimeout(() => {
        initFaceMesh(isVideoOff);
      }, 500);
    } else if (isMinimized && faceMeshInitialized) {
      stopFaceMesh();
    }
  }, [isMinimized]);

  // Reattach video stream when video element becomes available
  useEffect(() => {
    if (localStreamRef.current && localVideoRef.current) {
      if (localVideoRef.current.srcObject !== localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
        addDebugLog("Reattached local stream to video element");
      }
    }
  }, [isMinimized, localVideoRef.current]);

  // Initialize Web Audio API for voice analysis
  const initAudioAnalytics = async (stream) => {
    addDebugLog("Initializing Web Audio API...");
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      audioAnalyserRef.current = audioContextRef.current.createAnalyser();
      audioAnalyserRef.current.fftSize = 2048;
      
      audioSourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      audioSourceRef.current.connect(audioAnalyserRef.current);
      
      addDebugLog("Web Audio API initialized successfully");
      
      const resumeAudioContext = async () => {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
          addDebugLog(`Audio context resumed. State: ${audioContextRef.current.state}`);
          setAudioInitialized(true);
          startAudioAnalysis();
          document.removeEventListener('click', resumeAudioContext);
          document.removeEventListener('touchstart', resumeAudioContext);
        }
      };
      
      document.addEventListener('click', resumeAudioContext);
      document.addEventListener('touchstart', resumeAudioContext);
      
      if (audioContextRef.current.state === 'running') {
        setAudioInitialized(true);
        startAudioAnalysis();
      }
      
    } catch (err) {
      addDebugLog(`Error initializing Audio API: ${err.message}`);
    }
  };

  const startAudioAnalysis = () => {
    if (!audioAnalyserRef.current || !audioContextRef.current) return;
    if (audioContextRef.current.state !== 'running') return;
    
    addDebugLog("Starting audio analysis...");
    
    const dataArray = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
    const timeDomainArray = new Uint8Array(audioAnalyserRef.current.fftSize);
    let lastVoiceActivity = false;
    
    const analyzeAudio = () => {
      if (!audioAnalyserRef.current || !audioContextRef.current) return;
      
      try {
        audioAnalyserRef.current.getByteFrequencyData(dataArray);
        audioAnalyserRef.current.getByteTimeDomainData(timeDomainArray);
        
        const energy = calculateEnergy(dataArray);
        const isSpeaking = energy > 0.1;
        
        if (isSpeaking !== lastVoiceActivity) {
          if (isSpeaking && !lastVoiceActivity) {
            const now = Date.now();
            if (lastInterruptTimeRef.current && (now - lastInterruptTimeRef.current) < 500) {
              currentVoiceMetrics.current.interrupt_patterns = 1;
            } else {
              currentVoiceMetrics.current.interrupt_patterns = 0;
            }
            lastInterruptTimeRef.current = now;
          }
          lastVoiceActivity = isSpeaking;
        }
        
        const pitch = calculatePitch(timeDomainArray, audioContextRef.current.sampleRate);
        currentVoiceMetrics.current.pitch_variability = calculatePitchVariability(pitch);
        currentVoiceMetrics.current.speech_rate = calculateSpeechRate(isSpeaking);
        currentVoiceMetrics.current.pauses = calculatePauses(isSpeaking);
        currentVoiceMetrics.current.vocal_tremor = calculateTremor();
        currentVoiceMetrics.current.warmth_resonance = calculateWarmthResonance(dataArray);
        
        updatePitchHistory(pitch);
        detectSpeech(isSpeaking);
        
      } catch (err) {
        // Silently fail
      }
      
      audioAnalysisFrameRef.current = requestAnimationFrame(analyzeAudio);
    };
    
    analyzeAudio();
  };

  const calculateEnergy = (dataArray) => {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    return Math.min(1, average / 255);
  };

  const calculatePitch = (timeDomainArray, sampleRate) => {
    let maxCorr = 0;
    let pitch = 120;
    
    for (let lag = 20; lag < Math.min(1000, timeDomainArray.length - 100); lag++) {
      let correlation = 0;
      for (let i = 0; i < timeDomainArray.length - lag; i++) {
        correlation += (timeDomainArray[i] - 128) * (timeDomainArray[i + lag] - 128);
      }
      correlation /= (timeDomainArray.length - lag);
      
      if (correlation > maxCorr && correlation > 0) {
        maxCorr = correlation;
        const candidatePitch = sampleRate / lag;
        if (candidatePitch > 80 && candidatePitch < 300) {
          pitch = candidatePitch;
        }
      }
    }
    
    return Math.min(250, Math.max(85, pitch));
  };

  const calculatePitchVariability = (currentPitch) => {
    if (pitchHistoryRef.current.length < 2) return 0.5;
    const recentPitches = [...pitchHistoryRef.current.slice(-10), currentPitch];
    const mean = recentPitches.reduce((a, b) => a + b, 0) / recentPitches.length;
    const variance = recentPitches.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / recentPitches.length;
    return Math.min(0.9, Math.sqrt(variance) / 50);
  };

  const updatePitchHistory = (pitch) => {
    pitchHistoryRef.current.push(pitch);
    if (pitchHistoryRef.current.length > 100) {
      pitchHistoryRef.current.shift();
    }
  };

  const detectSpeech = (isSpeaking) => {
    const now = Date.now();
    if (isSpeaking) {
      if (pauseStartTimeRef.current) {
        pauseStartTimeRef.current = null;
      }
      speechCountRef.current++;
    } else {
      if (!pauseStartTimeRef.current) {
        pauseStartTimeRef.current = now;
      }
    }
  };

  const calculateSpeechRate = (isSpeaking) => {
    const now = Date.now();
    const timeElapsed = (now - lastSpeechTimeRef.current) / 1000;
    
    if (timeElapsed >= 1) {
      const speechRate = (speechCountRef.current / timeElapsed) * 60;
      speechCountRef.current = 0;
      lastSpeechTimeRef.current = now;
      return Math.min(180, Math.max(80, speechRate));
    }
    return 120;
  };

  const calculatePauses = (isSpeaking) => {
    if (!pauseStartTimeRef.current || isSpeaking) return 0.15;
    const pauseDuration = (Date.now() - pauseStartTimeRef.current) / 1000;
    return Math.min(0.4, pauseDuration / 15);
  };

  const calculateTremor = () => {
    if (pitchHistoryRef.current.length < 10) return 0.12;
    const recentPitches = pitchHistoryRef.current.slice(-10);
    const mean = recentPitches.reduce((a, b) => a + b, 0) / recentPitches.length;
    const variations = recentPitches.map(p => Math.abs(p - mean));
    const avgVariation = variations.reduce((a, b) => a + b, 0) / variations.length;
    return Math.min(0.3, avgVariation / 80);
  };

  const calculateWarmthResonance = (dataArray) => {
    const lowFreqSum = dataArray.slice(0, 100).reduce((a, b) => a + b, 0);
    const totalSum = dataArray.reduce((a, b) => a + b, 0);
    return Math.min(0.85, Math.max(0.4, lowFreqSum / (totalSum || 1)));
  };

  // Expose analytics data to parent
  useImperativeHandle(ref, () => ({
    getAnalyticsData: () => ({
      cameraMetrics: cameraMetricsHistory.current,
      voiceMetrics: voiceMetricsHistory.current
    })
  }));

  // Toggle functions with correct logic
  const toggleMute = () => {
    console.log("localStreamRef.current: ", localStreamRef.current);
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      console.log("audioTracks : ", audioTracks);
      console.log("Current isMuted : ", isMuted);
      
      const newMutedState = !isMuted;
      console.log("newMutedState : ", newMutedState);
      
      audioTracks.forEach(track => {
        track.enabled = !newMutedState;
        console.log(`Audio track "${track.label}" enabled: ${track.enabled}`);
      });
      
      setIsMuted(newMutedState);
      addDebugLog(`Audio ${newMutedState ? 'muted' : 'unmuted'}`);
      
      if (newMutedState) {
        if (audioContextRef.current && audioContextRef.current.state === 'running') {
          audioContextRef.current.suspend();
          console.log("Audio context suspended");
        }
      } else {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
          console.log("Audio context resumed");
        }
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      const newVideoState = !isVideoOff;
      
      videoTracks.forEach(track => {
        track.enabled = !newVideoState;
      });
      
      setIsVideoOff(newVideoState);
      addDebugLog(`Video ${newVideoState ? 'off' : 'on'}`);
      
      if (newVideoState) {
        stopFaceMesh();
      } else {
        if (!isMinimized) {
          setTimeout(() => {
            initFaceMesh(false);
          }, 100);
        }
      }
    }
  };

  const handleEndMeeting = async () => {
    if (isEnding) return;
    setIsEnding(true);
    addDebugLog("Ending meeting...");
    
   if (onMeetingEnd) {
  await onMeetingEnd();
}

cleanup();
  };

  const cleanup = () => {
    addDebugLog("Cleaning up VideoCall resources...");
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioAnalysisFrameRef.current) cancelAnimationFrame(audioAnalysisFrameRef.current);
    if (collectionIntervalRef.current) clearInterval(collectionIntervalRef.current);
    
    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (err) {}
      cameraRef.current = null;
    }
    if (faceMeshRef.current) {
      try {
        faceMeshRef.current.close();
      } catch (err) {}
      faceMeshRef.current = null;
    }
    
    if (peerRef.current) {
      try {
        peerRef.current.close();
      } catch (err) {}
      peerRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (err) {}
      audioContextRef.current = null;
    }
    
    if (socketRef.current) {
      try {
        socketRef.current.emit("leave-video-room", { roomId: String(roomId) });
        socketRef.current.disconnect();
      } catch (err) {}
      socketRef.current = null;
    }
  };

  // WebRTC functions
 const startCall = async () => {
  try {
    console.log(`Starting video call for roomId: ${roomId}`);

    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });

    localStreamRef.current = stream;

    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }

    await initAudioAnalytics(stream);

    createPeerConnection();

    stream.getTracks().forEach((track) => {
      peerRef.current.addTrack(track, stream);
    });

    socketRef.current.emit("join-video-room", {
      roomId: String(roomId),
    });

    setTimeout(() => {
      if (!isMinimized) initFaceMesh(isVideoOff);
    }, 1000);
  } catch (err) {
    console.log("startCall error:", err);
    setError(`Failed to access camera/microphone: ${err.message}`);
  }
};

 const createPeerConnection = () => {
  peerRef.current = new RTCPeerConnection({
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
    ],
  });

  peerRef.current.onicecandidate = (event) => {
    if (event.candidate) {
      socketRef.current.emit("ice-candidate", {
        roomId: String(roomId),
        candidate: event.candidate,
      });
    }
  };

  peerRef.current.ontrack = (event) => {
    console.log("✅ Remote track received", event.streams);

    if (remoteVideoRef.current && event.streams[0]) {
      remoteVideoRef.current.srcObject = event.streams[0];

      remoteVideoRef.current
        .play()
        .catch((err) => console.log("Remote video play error:", err));
    }
  };

  peerRef.current.onconnectionstatechange = () => {
    console.log("Peer connection state:", peerRef.current.connectionState);
  };

  peerRef.current.oniceconnectionstatechange = () => {
    console.log("ICE state:", peerRef.current.iceConnectionState);
  };
};

const setupSocketListeners = () => {
  const socket = socketRef.current;
  if (!socket) return;

  socket.on("room-users", async ({ count }) => {
    console.log("Room users count:", count);

    // second joined user creates offer
    if (count >= 2) {
      try {
        if (!peerRef.current) createPeerConnection();

        const offer = await peerRef.current.createOffer();
        await peerRef.current.setLocalDescription(offer);

        socket.emit("video-offer", {
          roomId: String(roomId),
          offer,
        });

        console.log("✅ video-offer sent");
        setIsCallActive(true);
      } catch (err) {
        console.log("Offer error:", err);
      }
    }
  });

  socket.on("video-offer", async ({ offer }) => {
    console.log("✅ video-offer received");

    try {
      if (!peerRef.current) createPeerConnection();

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          const exists = peerRef.current
            .getSenders()
            .some((sender) => sender.track === track);

          if (!exists) {
            peerRef.current.addTrack(track, localStreamRef.current);
          }
        });
      }

      await peerRef.current.setRemoteDescription(
        new RTCSessionDescription(offer)
      );

      const answer = await peerRef.current.createAnswer();
      await peerRef.current.setLocalDescription(answer);

      socket.emit("video-answer", {
        roomId: String(roomId),
        answer,
      });

      console.log("✅ video-answer sent");
      setIsCallActive(true);
    } catch (err) {
      console.log("video-offer handle error:", err);
    }
  });

  socket.on("video-answer", async ({ answer }) => {
    console.log("✅ video-answer received");

    try {
      if (
        peerRef.current &&
        peerRef.current.signalingState !== "stable"
      ) {
        await peerRef.current.setRemoteDescription(
          new RTCSessionDescription(answer)
        );
      }
    } catch (err) {
      console.log("video-answer error:", err);
    }
  });

  socket.on("ice-candidate", async ({ candidate }) => {
    try {
      if (peerRef.current && candidate) {
        await peerRef.current.addIceCandidate(
          new RTCIceCandidate(candidate)
        );
      }
    } catch (err) {
      console.log("ICE error:", err);
    }
  });

  socket.on("user-disconnected", () => {
    console.log("Remote user disconnected");
  });
};

  // Main effect to initialize socket and WebRTC
  useEffect(() => {
    if (!roomId || !userId) return;
    
  socketRef.current = io(config.baseURL, {
  auth: { token, userId: String(userId) },
  transports: ["websocket", "polling"],
});
    socketRef.current.on("connect", () => { 
      setIsConnected(true); 
      startCall(); 
    });
    socketRef.current.on("connect_error", (err) => setError(`Connection failed: ${err.message}`));
    setupSocketListeners();
    
    return () => {
      cleanup();
    };
  }, [roomId, userId]);

  // Data collection interval
  useEffect(() => {
    if (!isCallActive) return;
    addCameraDataPoint();
    addVoiceDataPoint();
    collectionIntervalRef.current = setInterval(() => {
      addCameraDataPoint();
      addVoiceDataPoint();
    }, 60000);
    return () => {
      if (collectionIntervalRef.current) clearInterval(collectionIntervalRef.current);
    };
  }, [isCallActive]);

  if (error) return <div className="vc-error-state"><p>{error}</p></div>;

  // Minimized view - showing remote video instead of local
if (isMinimized) {
  return (
    <div className="vc-minimized">
     <div className="vc-minimized-header">
  <span>Meeting with {selectedUser?.first_name || "User"}</span>

  <button
    type="button"
    className="vc-mini-expand-btn"
    onClick={() => {
      if (typeof onToggleMinimize === "function") {
        onToggleMinimize();
      }
    }}
    title="Restore"
  >
    <FaExpandAlt />
  </button>
</div>

      <div className="vc-minimized-content">
        <div className="vc-mini-avatar">
          {selectedUser?.first_name?.charAt(0) || "U"}
        </div>
        <div className="vc-mini-name">
          {selectedUser?.first_name || "Remote User"}
        </div>
      </div>

      <div className="vc-minimized-controls">
        <button type="button" onClick={toggleMute} className="vc-mini-control">
          {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </button>

        <button type="button" onClick={toggleVideo} className="vc-mini-control">
          {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
        </button>

        <button type="button" onClick={handleEndMeeting} className="vc-mini-control vc-mini-end">
          <MdCallEnd />
        </button>
      </div>
    </div>
  );
}

  // Full view - Your video on left, remote on right
  return (
    <div className="vc-container">
      <div className="vc-video-grid">
        <div className="vc-video-participant vc-self-view">
          <video ref={localVideoRef} autoPlay muted playsInline className="vc-video-element" />
          <div className="vc-participant-label">
            You {isMuted && <FaMicrophoneSlash className="vc-mic-off-icon" />}
          </div>
          {isVideoOff && (
            <div className="vc-video-off-overlay">
              <FaVideoSlash />
            </div>
          )}
        </div>
        <div className="vc-video-participant">
          <video ref={remoteVideoRef} autoPlay playsInline className="vc-video-element" />
          <div className="vc-participant-label">
            {selectedUser?.first_name || 'Remote User'}
          </div>
        </div>
      </div>

      <div className="vc-controls-bar">
        <div className="vc-controls-left"></div>
        
        <div className="vc-controls-center">
          <button 
            className={`vc-control-btn ${isMuted ? 'active' : ''}`} 
            onClick={toggleMute}
          >
            {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>
          
          <button 
            className={`vc-control-btn ${isVideoOff ? 'active' : ''}`} 
            onClick={toggleVideo}
          >
            {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
          </button>
          
          <button 
            className="vc-control-btn vc-end-btn" 
            onClick={handleEndMeeting}
            disabled={isEnding}
          >
            <MdCallEnd />
          </button>
        </div>
        
        <div className="vc-controls-right">
          <button 
            className="vc-control-btn vc-minimize-btn" 
            onClick={onToggleMinimize}
          >
            <FaWindowMinimize />
          </button>
        </div>
      </div>
    </div>
  );
});

export default VideoCall;





// // VideoCall.jsx - Design only changes, functionality preserved
// import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
// import io from "socket.io-client";
// import config from "../../../config";
// import { Camera } from "@mediapipe/camera_utils";
// import { FaceMesh } from "@mediapipe/face_mesh";
// import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaWindowMinimize, FaWindowMaximize } from "react-icons/fa";
// import { MdCallEnd } from "react-icons/md";
// import { toast } from "../../../components/common/Toast";

// // Import the new CSS
// import "./VideoCall.css";

// const VideoCall = forwardRef(({ 
//   roomId, 
//   userId, 
//   token, 
//   meetingId, 
//   user, 
//   selectedUser, 
//   user1Id, 
//   user2Id, 
//   onMeetingEnd,
//   isMinimized = false,
//   onToggleMinimize
// }, ref) => {
//   // Refs
//   const localVideoRef = useRef();
//   const remoteVideoRef = useRef();
//   const peerRef = useRef();
//   const localStreamRef = useRef();
//   const socketRef = useRef();
//   const [isConnected, setIsConnected] = useState(false);
//   const [isCallActive, setIsCallActive] = useState(false);
//   const [error, setError] = useState(null);
//   const [isMuted, setIsMuted] = useState(false);
//   const [isVideoOff, setIsVideoOff] = useState(false);
//   const [audioInitialized, setAudioInitialized] = useState(false);
//   const [isEnding, setIsEnding] = useState(false);
//   const [faceMeshInitialized, setFaceMeshInitialized] = useState(false);

//   // Analytics refs
//   const cameraMetricsHistory = useRef({
//     eye_contact_duration: [],
//     blink_rate: [],
//     facial_tension: [],
//     smile_authenticity: [],
//     head_tilt_openness: [],
//     lean_posture: [],
//     micro_joy: [],
//     micro_fear: [],
//     micro_disgust: [],
//     micro_surprise: []
//   });

//   const voiceMetricsHistory = useRef({
//     pitch_variability: [],
//     speech_rate: [],
//     pauses: [],
//     vocal_tremor: [],
//     warmth_resonance: [],
//     interrupt_patterns: []
//   });

//   const lastCameraMinuteRef = useRef(null);
//   const lastVoiceMinuteRef = useRef(null);
  
//   const currentCameraMetrics = useRef({
//     eye_contact_duration: 0.5,
//     blink_rate: 0,
//     facial_tension: 0.5,
//     smile_authenticity: 0.5,
//     head_tilt_openness: 0.5,
//     lean_posture: 0.5,
//     micro_joy: 0.5,
//     micro_fear: 0.1,
//     micro_disgust: 0.05,
//     micro_surprise: 0.2
//   });

//   const currentVoiceMetrics = useRef({
//     pitch_variability: 0.5,
//     speech_rate: 0,
//     pauses: 0,
//     vocal_tremor: 0.1,
//     warmth_resonance: 0.5,
//     interrupt_patterns: 0
//   });

//   // Refs for analytics
//   const faceMeshRef = useRef(null);
//   const cameraRef = useRef(null);
//   const animationFrameRef = useRef(null);
//   const lastBlinkTimeRef = useRef(null);
//   const blinkCountRef = useRef(0);
//   const lastFrameTimeRef = useRef(Date.now());
//   const audioContextRef = useRef(null);
//   const audioAnalyserRef = useRef(null);
//   const audioSourceRef = useRef(null);
//   const lastSpeechTimeRef = useRef(Date.now());
//   const speechCountRef = useRef(0);
//   const pauseStartTimeRef = useRef(null);
//   const pitchHistoryRef = useRef([]);
//   const lastInterruptTimeRef = useRef(null);
//   const collectionIntervalRef = useRef(null);
//   const audioAnalysisFrameRef = useRef(null);
//   const lastFaceDetectionLog = useRef(Date.now());

//   const addDebugLog = (message) => console.log(message);

//   const getCurrentMinuteTimestamp = () => Math.floor(Date.now() / 1000 / 60) * 60;
  
//   const addCameraDataPoint = () => {
//     const timestamp = getCurrentMinuteTimestamp();
//     if (lastCameraMinuteRef.current === timestamp) return;
//     lastCameraMinuteRef.current = timestamp;
    
//     Object.keys(cameraMetricsHistory.current).forEach(key => {
//       let value = currentCameraMetrics.current[key];
//       if (typeof value !== 'number') value = 0.5;
//       if (key === 'blink_rate') value = Math.min(30, Math.max(0, value));
      
//       cameraMetricsHistory.current[key].push({
//         ts: timestamp,
//         value: value
//       });
//     });
//   };
  
//   const addVoiceDataPoint = () => {
//     const timestamp = getCurrentMinuteTimestamp();
//     if (lastVoiceMinuteRef.current === timestamp) return;
//     lastVoiceMinuteRef.current = timestamp;
    
//     Object.keys(voiceMetricsHistory.current).forEach(key => {
//       let value = currentVoiceMetrics.current[key];
//       if (typeof value !== 'number') value = key === 'interrupt_patterns' ? 0 : 0.5;
      
//       voiceMetricsHistory.current[key].push({
//         ts: timestamp,
//         value: value
//       });
//     });
//   };

//   const processFaceAnalytics = (landmarks) => {
//     const now = Date.now();
    
//     currentCameraMetrics.current.eye_contact_duration = calculateEyeContact(landmarks);
//     currentCameraMetrics.current.blink_rate = calculateBlinkRate(now);
//     currentCameraMetrics.current.facial_tension = calculateFacialTension(landmarks);
//     currentCameraMetrics.current.smile_authenticity = calculateSmileAuthenticity(landmarks);
//     currentCameraMetrics.current.head_tilt_openness = calculateHeadTilt(landmarks);
//     currentCameraMetrics.current.lean_posture = calculateLeanPosture(landmarks);
    
//     const microExpressions = detectMicroExpressions(landmarks);
//     currentCameraMetrics.current.micro_joy = microExpressions.joy;
//     currentCameraMetrics.current.micro_fear = microExpressions.fear;
//     currentCameraMetrics.current.micro_disgust = microExpressions.disgust;
//     currentCameraMetrics.current.micro_surprise = microExpressions.surprise;
    
//     detectBlink(landmarks);
    
//     if (now - lastFaceDetectionLog.current > 10000) {
//       addDebugLog(`✅ Face detected! Eye: ${currentCameraMetrics.current.eye_contact_duration.toFixed(3)}`);
//       lastFaceDetectionLog.current = now;
//     }
//   };

//   const calculateEyeContact = (landmarks) => {
//     const leftEyeInner = landmarks[133];
//     const rightEyeInner = landmarks[362];
//     if (leftEyeInner && rightEyeInner) {
//       const gazeScore = Math.abs(leftEyeInner.x - rightEyeInner.x);
//       return Math.min(0.95, Math.max(0.3, 0.7 + gazeScore * 0.3));
//     }
//     return 0.5;
//   };
  
//   const calculateBlinkRate = (now) => {
//     const timeElapsed = (now - lastFrameTimeRef.current) / 1000;
//     if (timeElapsed >= 1) {
//       const blinkRate = (blinkCountRef.current / timeElapsed) * 60;
//       blinkCountRef.current = 0;
//       lastFrameTimeRef.current = now;
//       return Math.min(25, Math.max(8, blinkRate));
//     }
//     return 12;
//   };

//   const calculateFacialTension = (landmarks) => {
//     const browLeft = landmarks[70];
//     const browRight = landmarks[300];
//     if (browLeft && browRight) {
//       const tension = Math.abs(browLeft.y - browRight.y);
//       return Math.min(0.9, Math.max(0.1, tension * 10));
//     }
//     return 0.5;
//   };

//   const calculateSmileAuthenticity = (landmarks) => {
//     const mouthLeft = landmarks[61];
//     const mouthRight = landmarks[291];
//     const mouthTop = landmarks[13];
//     const mouthBottom = landmarks[14];
//     if (mouthLeft && mouthRight && mouthTop && mouthBottom) {
//       const mouthWidth = Math.abs(mouthLeft.x - mouthRight.x);
//       const mouthHeight = Math.abs(mouthTop.y - mouthBottom.y);
//       const smileRatio = mouthHeight / (mouthWidth + 0.01);
//       return Math.min(0.95, Math.max(0.3, smileRatio * 3));
//     }
//     return 0.5;
//   };

//   const calculateHeadTilt = (landmarks) => {
//     const nose = landmarks[1];
//     const leftEye = landmarks[33];
//     const rightEye = landmarks[263];
//     if (nose && leftEye && rightEye) {
//       const eyeCenterY = (leftEye.y + rightEye.y) / 2;
//       const tilt = nose.y - eyeCenterY;
//       return Math.min(0.9, Math.max(0.3, 0.6 + tilt * 2));
//     }
//     return 0.5;
//   };

//   const calculateLeanPosture = (landmarks) => {
//     const leftShoulder = landmarks[11];
//     const rightShoulder = landmarks[12];
//     if (leftShoulder && rightShoulder) {
//       const shoulderWidth = Math.abs(leftShoulder.x - rightShoulder.x);
//       const leanScore = 1 - Math.min(1, shoulderWidth);
//       return Math.max(0.4, Math.min(0.9, leanScore + 0.4));
//     }
//     return 0.5;
//   };

//   const detectMicroExpressions = (landmarks) => {
//     const mouthLeft = landmarks[61];
//     const mouthRight = landmarks[291];
//     let joy = 0.5;
//     if (mouthLeft && mouthRight) {
//       const mouthWidth = Math.abs(mouthLeft.x - mouthRight.x);
//       joy = Math.min(0.95, Math.max(0.3, mouthWidth * 2));
//     }
    
//     const browLeft = landmarks[70];
//     const browRight = landmarks[300];
//     let fear = 0.1;
//     if (browLeft && browRight) {
//       fear = Math.min(0.9, Math.max(0.05, Math.abs(browLeft.y - browRight.y) * 5));
//     }
    
//     const nose = landmarks[1];
//     const mouthTop = landmarks[13];
//     let disgust = 0.05;
//     if (nose && mouthTop) {
//       disgust = Math.min(0.8, Math.max(0.03, Math.abs(nose.y - mouthTop.y) * 2));
//     }
    
//     const leftEye = landmarks[33];
//     const rightEye = landmarks[263];
//     const mouthBottom = landmarks[14];
//     let surprise = 0.2;
//     if (leftEye && rightEye && mouthTop && mouthBottom) {
//       const eyeOpenness = (Math.abs(leftEye.y - landmarks[159].y) + Math.abs(rightEye.y - landmarks[386].y)) / 2;
//       const mouthOpenness = Math.abs(mouthTop.y - mouthBottom.y);
//       surprise = Math.min(0.95, Math.max(0.1, (eyeOpenness + mouthOpenness) * 5));
//     }
    
//     return { joy, fear, disgust, surprise };
//   };

//   const detectBlink = (landmarks) => {
//     const now = Date.now();
//     if (!lastBlinkTimeRef.current) {
//       lastBlinkTimeRef.current = now;
//       return;
//     }
    
//     const leftEyeTop = landmarks[159];
//     const leftEyeBottom = landmarks[145];
//     const rightEyeTop = landmarks[386];
//     const rightEyeBottom = landmarks[374];
    
//     if (leftEyeTop && leftEyeBottom && rightEyeTop && rightEyeBottom) {
//       const leftEyeOpen = Math.abs(leftEyeTop.y - leftEyeBottom.y);
//       const rightEyeOpen = Math.abs(rightEyeTop.y - rightEyeBottom.y);
//       const isBlinking = (leftEyeOpen < 0.01 || rightEyeOpen < 0.01);
      
//       if (isBlinking && (now - lastBlinkTimeRef.current) > 200) {
//         blinkCountRef.current++;
//         lastBlinkTimeRef.current = now;
//       }
//     }
//   };

//   // Stop FaceMesh and camera
//   const stopFaceMesh = async () => {
//     if (cameraRef.current) {
//       try {
//         await cameraRef.current.stop();
//       } catch (err) {
//         console.log(`Error stopping camera: ${err.message}`);
//       }
//       cameraRef.current = null;
//     }
    
//     if (faceMeshRef.current) {
//       try {
//         await faceMeshRef.current.close();
//       } catch (err) {
//         console.log(`Error closing FaceMesh: ${err.message}`);
//       }
//       faceMeshRef.current = null;
//     }
    
//     if (animationFrameRef.current) {
//       cancelAnimationFrame(animationFrameRef.current);
//       animationFrameRef.current = null;
//     }
    
//     setFaceMeshInitialized(false);
//   };

//   // Initialize FaceMesh (safe version with error handling)
//   const initFaceMesh = async (forceVideoOff = null) => {
//     const videoIsOff = forceVideoOff !== null ? forceVideoOff : isVideoOff;
    
//     if (videoIsOff) {
//       console.log("Video is off, skipping FaceMesh initialization");
//       return;
//     }
    
//     if (faceMeshInitialized) {
//       console.log("FaceMesh already initialized");
//       return;
//     }
    
//     if (!localVideoRef.current || !localStreamRef.current) {
//       console.log("Cannot initialize FaceMesh: video element or stream not ready");
//       return;
//     }
    
//     console.log("Initializing MediaPipe FaceMesh...");
    
//     try {
//       await stopFaceMesh();
      
//       faceMeshRef.current = new FaceMesh({
//         locateFile: (file) => {
//           return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
//         }
//       });
      
//       faceMeshRef.current.setOptions({
//         maxNumFaces: 1,
//         refineLandmarks: true,
//         minDetectionConfidence: 0.5,
//         minTrackingConfidence: 0.5
//       });
      
//       faceMeshRef.current.onResults((results) => {
//         if (isVideoOff) return;
        
//         if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0 && !isMinimized) {
//           const landmarks = results.multiFaceLandmarks[0];
//           processFaceAnalytics(landmarks);
//         }
//       });
      
//       if (localVideoRef.current && localStreamRef.current && !isMinimized && !videoIsOff) {
//         cameraRef.current = new Camera(localVideoRef.current, {
//           onFrame: async () => {
//             if (isVideoOff) return;
            
//             if (faceMeshRef.current && localVideoRef.current && !isMinimized) {
//               try {
//                 await faceMeshRef.current.send({image: localVideoRef.current});
//               } catch (err) {
//                 // Silently fail
//               }
//             }
//           },
//           width: 640,
//           height: 480
//         });
//         await cameraRef.current.start();
//         console.log("Camera started successfully");
//         setFaceMeshInitialized(true);
//       }
//     } catch (err) {
//       console.log(`Error initializing FaceMesh: ${err.message}`);
//       setFaceMeshInitialized(false);
//     }
//   };

//   // Re-initialize FaceMesh when coming back from minimized state
//   useEffect(() => {
//     if (!isMinimized && localStreamRef.current && localVideoRef.current && !faceMeshInitialized) {
//       setTimeout(() => {
//         initFaceMesh(isVideoOff);
//       }, 500);
//     } else if (isMinimized && faceMeshInitialized) {
//       stopFaceMesh();
//     }
//   }, [isMinimized]);

//   // Reattach video stream when video element becomes available
//   useEffect(() => {
//     if (localStreamRef.current && localVideoRef.current) {
//       if (localVideoRef.current.srcObject !== localStreamRef.current) {
//         localVideoRef.current.srcObject = localStreamRef.current;
//         addDebugLog("Reattached local stream to video element");
//       }
//     }
//   }, [isMinimized, localVideoRef.current]);

//   // Initialize Web Audio API for voice analysis
//   const initAudioAnalytics = async (stream) => {
//     addDebugLog("Initializing Web Audio API...");
//     try {
//       audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
//       audioAnalyserRef.current = audioContextRef.current.createAnalyser();
//       audioAnalyserRef.current.fftSize = 2048;
      
//       audioSourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
//       audioSourceRef.current.connect(audioAnalyserRef.current);
      
//       addDebugLog("Web Audio API initialized successfully");
      
//       const resumeAudioContext = async () => {
//         if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
//           await audioContextRef.current.resume();
//           addDebugLog(`Audio context resumed. State: ${audioContextRef.current.state}`);
//           setAudioInitialized(true);
//           startAudioAnalysis();
//           document.removeEventListener('click', resumeAudioContext);
//           document.removeEventListener('touchstart', resumeAudioContext);
//         }
//       };
      
//       document.addEventListener('click', resumeAudioContext);
//       document.addEventListener('touchstart', resumeAudioContext);
      
//       if (audioContextRef.current.state === 'running') {
//         setAudioInitialized(true);
//         startAudioAnalysis();
//       }
      
//     } catch (err) {
//       addDebugLog(`Error initializing Audio API: ${err.message}`);
//     }
//   };

//   const startAudioAnalysis = () => {
//     if (!audioAnalyserRef.current || !audioContextRef.current) return;
//     if (audioContextRef.current.state !== 'running') return;
    
//     addDebugLog("Starting audio analysis...");
    
//     const dataArray = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
//     const timeDomainArray = new Uint8Array(audioAnalyserRef.current.fftSize);
//     let lastVoiceActivity = false;
    
//     const analyzeAudio = () => {
//       if (!audioAnalyserRef.current || !audioContextRef.current) return;
      
//       try {
//         audioAnalyserRef.current.getByteFrequencyData(dataArray);
//         audioAnalyserRef.current.getByteTimeDomainData(timeDomainArray);
        
//         const energy = calculateEnergy(dataArray);
//         const isSpeaking = energy > 0.1;
        
//         if (isSpeaking !== lastVoiceActivity) {
//           if (isSpeaking && !lastVoiceActivity) {
//             const now = Date.now();
//             if (lastInterruptTimeRef.current && (now - lastInterruptTimeRef.current) < 500) {
//               currentVoiceMetrics.current.interrupt_patterns = 1;
//             } else {
//               currentVoiceMetrics.current.interrupt_patterns = 0;
//             }
//             lastInterruptTimeRef.current = now;
//           }
//           lastVoiceActivity = isSpeaking;
//         }
        
//         const pitch = calculatePitch(timeDomainArray, audioContextRef.current.sampleRate);
//         currentVoiceMetrics.current.pitch_variability = calculatePitchVariability(pitch);
//         currentVoiceMetrics.current.speech_rate = calculateSpeechRate(isSpeaking);
//         currentVoiceMetrics.current.pauses = calculatePauses(isSpeaking);
//         currentVoiceMetrics.current.vocal_tremor = calculateTremor();
//         currentVoiceMetrics.current.warmth_resonance = calculateWarmthResonance(dataArray);
        
//         updatePitchHistory(pitch);
//         detectSpeech(isSpeaking);
        
//       } catch (err) {
//         // Silently fail
//       }
      
//       audioAnalysisFrameRef.current = requestAnimationFrame(analyzeAudio);
//     };
    
//     analyzeAudio();
//   };

//   const calculateEnergy = (dataArray) => {
//     let sum = 0;
//     for (let i = 0; i < dataArray.length; i++) {
//       sum += dataArray[i];
//     }
//     const average = sum / dataArray.length;
//     return Math.min(1, average / 255);
//   };

//   const calculatePitch = (timeDomainArray, sampleRate) => {
//     let maxCorr = 0;
//     let pitch = 120;
    
//     for (let lag = 20; lag < Math.min(1000, timeDomainArray.length - 100); lag++) {
//       let correlation = 0;
//       for (let i = 0; i < timeDomainArray.length - lag; i++) {
//         correlation += (timeDomainArray[i] - 128) * (timeDomainArray[i + lag] - 128);
//       }
//       correlation /= (timeDomainArray.length - lag);
      
//       if (correlation > maxCorr && correlation > 0) {
//         maxCorr = correlation;
//         const candidatePitch = sampleRate / lag;
//         if (candidatePitch > 80 && candidatePitch < 300) {
//           pitch = candidatePitch;
//         }
//       }
//     }
    
//     return Math.min(250, Math.max(85, pitch));
//   };

//   const calculatePitchVariability = (currentPitch) => {
//     if (pitchHistoryRef.current.length < 2) return 0.5;
//     const recentPitches = [...pitchHistoryRef.current.slice(-10), currentPitch];
//     const mean = recentPitches.reduce((a, b) => a + b, 0) / recentPitches.length;
//     const variance = recentPitches.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / recentPitches.length;
//     return Math.min(0.9, Math.sqrt(variance) / 50);
//   };

//   const updatePitchHistory = (pitch) => {
//     pitchHistoryRef.current.push(pitch);
//     if (pitchHistoryRef.current.length > 100) {
//       pitchHistoryRef.current.shift();
//     }
//   };

//   const detectSpeech = (isSpeaking) => {
//     const now = Date.now();
//     if (isSpeaking) {
//       if (pauseStartTimeRef.current) {
//         pauseStartTimeRef.current = null;
//       }
//       speechCountRef.current++;
//     } else {
//       if (!pauseStartTimeRef.current) {
//         pauseStartTimeRef.current = now;
//       }
//     }
//   };

//   const calculateSpeechRate = (isSpeaking) => {
//     const now = Date.now();
//     const timeElapsed = (now - lastSpeechTimeRef.current) / 1000;
    
//     if (timeElapsed >= 1) {
//       const speechRate = (speechCountRef.current / timeElapsed) * 60;
//       speechCountRef.current = 0;
//       lastSpeechTimeRef.current = now;
//       return Math.min(180, Math.max(80, speechRate));
//     }
//     return 120;
//   };

//   const calculatePauses = (isSpeaking) => {
//     if (!pauseStartTimeRef.current || isSpeaking) return 0.15;
//     const pauseDuration = (Date.now() - pauseStartTimeRef.current) / 1000;
//     return Math.min(0.4, pauseDuration / 15);
//   };

//   const calculateTremor = () => {
//     if (pitchHistoryRef.current.length < 10) return 0.12;
//     const recentPitches = pitchHistoryRef.current.slice(-10);
//     const mean = recentPitches.reduce((a, b) => a + b, 0) / recentPitches.length;
//     const variations = recentPitches.map(p => Math.abs(p - mean));
//     const avgVariation = variations.reduce((a, b) => a + b, 0) / variations.length;
//     return Math.min(0.3, avgVariation / 80);
//   };

//   const calculateWarmthResonance = (dataArray) => {
//     const lowFreqSum = dataArray.slice(0, 100).reduce((a, b) => a + b, 0);
//     const totalSum = dataArray.reduce((a, b) => a + b, 0);
//     return Math.min(0.85, Math.max(0.4, lowFreqSum / (totalSum || 1)));
//   };

//   // Expose analytics data to parent
//   useImperativeHandle(ref, () => ({
//     getAnalyticsData: () => ({
//       cameraMetrics: cameraMetricsHistory.current,
//       voiceMetrics: voiceMetricsHistory.current
//     })
//   }));

//   // Toggle functions with correct logic
//   const toggleMute = () => {
//     console.log("localStreamRef.current: ", localStreamRef.current);
//     if (localStreamRef.current) {
//       const audioTracks = localStreamRef.current.getAudioTracks();
//       console.log("audioTracks : ", audioTracks);
//       console.log("Current isMuted : ", isMuted);
      
//       const newMutedState = !isMuted;
//       console.log("newMutedState : ", newMutedState);
      
//       audioTracks.forEach(track => {
//         track.enabled = !newMutedState;
//         console.log(`Audio track "${track.label}" enabled: ${track.enabled}`);
//       });
      
//       setIsMuted(newMutedState);
//       addDebugLog(`Audio ${newMutedState ? 'muted' : 'unmuted'}`);
      
//       if (newMutedState) {
//         if (audioContextRef.current && audioContextRef.current.state === 'running') {
//           audioContextRef.current.suspend();
//           console.log("Audio context suspended");
//         }
//       } else {
//         if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
//           audioContextRef.current.resume();
//           console.log("Audio context resumed");
//         }
//       }
//     }
//   };

//   const toggleVideo = () => {
//     if (localStreamRef.current) {
//       const videoTracks = localStreamRef.current.getVideoTracks();
//       const newVideoState = !isVideoOff;
      
//       videoTracks.forEach(track => {
//         track.enabled = !newVideoState;
//       });
      
//       setIsVideoOff(newVideoState);
//       addDebugLog(`Video ${newVideoState ? 'off' : 'on'}`);
      
//       if (newVideoState) {
//         stopFaceMesh();
//       } else {
//         if (!isMinimized) {
//           setTimeout(() => {
//             initFaceMesh(false);
//           }, 100);
//         }
//       }
//     }
//   };

//   const handleEndMeeting = async () => {
//     if (isEnding) return;
//     setIsEnding(true);
//     addDebugLog("Ending meeting...");
    
//     if (onMeetingEnd) onMeetingEnd();
    
//     cleanup();
//   };

//   const cleanup = () => {
//     addDebugLog("Cleaning up VideoCall resources...");
//     if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
//     if (audioAnalysisFrameRef.current) cancelAnimationFrame(audioAnalysisFrameRef.current);
//     if (collectionIntervalRef.current) clearInterval(collectionIntervalRef.current);
    
//     if (cameraRef.current) {
//       try {
//         cameraRef.current.stop();
//       } catch (err) {}
//       cameraRef.current = null;
//     }
//     if (faceMeshRef.current) {
//       try {
//         faceMeshRef.current.close();
//       } catch (err) {}
//       faceMeshRef.current = null;
//     }
    
//     if (peerRef.current) {
//       try {
//         peerRef.current.close();
//       } catch (err) {}
//       peerRef.current = null;
//     }
    
//     if (localStreamRef.current) {
//       localStreamRef.current.getTracks().forEach(track => track.stop());
//       localStreamRef.current = null;
//     }
//     if (localVideoRef.current) localVideoRef.current.srcObject = null;
//     if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    
//     if (audioContextRef.current) {
//       try {
//         audioContextRef.current.close();
//       } catch (err) {}
//       audioContextRef.current = null;
//     }
    
//     if (socketRef.current) {
//       try {
//         socketRef.current.emit("leave-video-room", { roomId: String(roomId) });
//         socketRef.current.disconnect();
//       } catch (err) {}
//       socketRef.current = null;
//     }
//   };

//   // WebRTC functions
//   const startCall = async () => {
//     try {
//       addDebugLog(`Starting video call for roomId: ${roomId}`);
      
//       const stream = await navigator.mediaDevices.getUserMedia({
//         video: true,
//         audio: true,
//       });

//       addDebugLog(`Got media stream with ${stream.getTracks().length} tracks`);
//       localVideoRef.current.srcObject = stream;
//       localStreamRef.current = stream;

//       await initAudioAnalytics(stream);
      
//       setTimeout(() => {
//         if (!isMinimized) {
//           initFaceMesh(isVideoOff);
//         }
//       }, 1000);

//       const joinPayload = { roomId: String(roomId) };
//       socketRef.current.emit("join-video-room", joinPayload);

//       createPeerConnection();

//       stream.getTracks().forEach((track) => {
//         if (peerRef.current && track) {
//           peerRef.current.addTrack(track, stream);
//         }
//       });

//       setTimeout(async () => {
//         if (peerRef.current && peerRef.current.signalingState === "stable") {
//           addDebugLog("Creating WebRTC offer...");
//           const offer = await peerRef.current.createOffer();
//           await peerRef.current.setLocalDescription(offer);
          
//           socketRef.current.emit("video-offer", {
//             roomId: String(roomId),
//             offer,
//           });
//           setIsCallActive(true);
//           addDebugLog("Offer sent successfully");
//         }
//       }, 1000);
      
//     } catch (err) {
//       addDebugLog(`Error starting call: ${err.message}`);
//       setError(`Failed to access camera/microphone: ${err.message}`);
//     }
//   };

//   const createPeerConnection = () => {
//     peerRef.current = new RTCPeerConnection({
//       iceServers: [
//         { urls: "stun:stun.l.google.com:19302" },
//         { urls: "stun:stun1.l.google.com:19302" },
//         { urls: "stun:stun2.l.google.com:19302" },
//       ],
//     });

//     peerRef.current.onicecandidate = (event) => {
//       if (event.candidate) {
//         socketRef.current.emit("ice-candidate", {
//           roomId: String(roomId),
//           candidate: event.candidate,
//         });
//       }
//     };

//     peerRef.current.ontrack = (event) => {
//       if (remoteVideoRef.current) {
//         remoteVideoRef.current.srcObject = event.streams[0];
//       }
//     };
//   };

//   const setupSocketListeners = () => {
//     const socket = socketRef.current;
//     if (!socket) return;

//     socket.on("video-offer", async ({ offer }) => {
//       if (!peerRef.current) createPeerConnection();

//       try {
//         await peerRef.current.setRemoteDescription(new RTCSessionDescription(offer));
//         const answer = await peerRef.current.createAnswer();
//         await peerRef.current.setLocalDescription(answer);

//         socket.emit("video-answer", {
//           roomId: String(roomId),
//           answer,
//         });
//         setIsCallActive(true);
//       } catch (err) {
//         addDebugLog(`Error handling video offer: ${err.message}`);
//       }
//     });
    
//     socket.on("video-answer", async ({ answer }) => {
//       if (peerRef.current && peerRef.current.remoteDescription === null) {
//         try {
//           await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
//         } catch (err) {
//           addDebugLog(`Error setting remote description: ${err.message}`);
//         }
//       }
//     });
    
//     socket.on("ice-candidate", async ({ candidate }) => {
//       if (peerRef.current && candidate) {
//         try {
//           await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
//         } catch (err) {
//           addDebugLog(`Error adding ICE candidate: ${err.message}`);
//         }
//       }
//     });
    
//     socket.on("user-disconnected", ({ userId: disconnectedUserId }) => {
//       addDebugLog(`Remote user disconnected: ${disconnectedUserId}`);
//       if (selectedUser) {
//         toast.error(`${selectedUser.first_name} ${selectedUser.last_name} disconnected from call`);
//       } else {
//         toast.error("Remote user disconnected from call");
//       }
//       onMeetingEnd();
//     });
//   };

//   // Main effect to initialize socket and WebRTC
//   useEffect(() => {
//     if (!roomId || !userId) return;
    
//     socketRef.current = io(config.baseURL, { 
//       auth: { token }, 
//       transports: ["websocket", "polling"] 
//     });
//     socketRef.current.on("connect", () => { 
//       setIsConnected(true); 
//       startCall(); 
//     });
//     socketRef.current.on("connect_error", (err) => setError(`Connection failed: ${err.message}`));
//     setupSocketListeners();
    
//     return () => {
//       cleanup();
//     };
//   }, [roomId, userId]);

//   // Data collection interval
//   useEffect(() => {
//     if (!isCallActive) return;
//     addCameraDataPoint();
//     addVoiceDataPoint();
//     collectionIntervalRef.current = setInterval(() => {
//       addCameraDataPoint();
//       addVoiceDataPoint();
//     }, 60000);
//     return () => {
//       if (collectionIntervalRef.current) clearInterval(collectionIntervalRef.current);
//     };
//   }, [isCallActive]);

//   if (error) return <div className="vc-error-state"><p>{error}</p></div>;

//   // Minimized view
//   if (isMinimized) {
//     return (
//       <div className="vc-minimized">
//         <div className="vc-minimized-header">
//           <span className="vc-minimized-title">Meeting with {selectedUser?.first_name || 'User'}</span>
//           <button className="vc-minimized-maximize-btn" onClick={onToggleMinimize}>
//             <FaWindowMaximize />
//           </button>
//         </div>
//         <div className="vc-minimized-video">
//           <video ref={localVideoRef} autoPlay muted playsInline />
//           <div className="vc-minimized-label">You</div>
//         </div>
//         <div className="vc-minimized-controls">
//           <button onClick={toggleMute} className={`vc-minimized-control-btn ${isMuted ? 'active' : ''}`}>
//             {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
//           </button>
//           <button onClick={toggleVideo} className={`vc-minimized-control-btn ${isVideoOff ? 'active' : ''}`}>
//             {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
//           </button>
//           <button onClick={handleEndMeeting} className="vc-minimized-control-btn vc-minimized-end-btn">
//             <MdCallEnd />
//           </button>
//         </div>
//       </div>
//     );
//   }

//   // Full view
//   return (
//     <div className="vc-container">
//       <div className="vc-video-grid">
//         <div className="vc-video-participant">
//           <video ref={remoteVideoRef} autoPlay playsInline className="vc-video-element" />
//           <div className="vc-participant-label">
//             {selectedUser?.first_name || 'Remote User'}
//           </div>
//         </div>
//         <div className="vc-video-participant vc-self-view">
//           <video ref={localVideoRef} autoPlay muted playsInline className="vc-video-element" />
//           <div className="vc-participant-label">
//             You {isMuted && <FaMicrophoneSlash className="vc-mic-off-icon" />}
//           </div>
//           {isVideoOff && (
//             <div className="vc-video-off-overlay">
//               <FaVideoSlash />
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="vc-controls-bar">
//         <div className="vc-controls-left"></div>
        
//         <div className="vc-controls-center">
//           <button 
//             className={`vc-control-btn ${isMuted ? 'active' : ''}`} 
//             onClick={toggleMute}
//           >
//             {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
//           </button>
          
//           <button 
//             className={`vc-control-btn ${isVideoOff ? 'active' : ''}`} 
//             onClick={toggleVideo}
//           >
//             {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
//           </button>
          
//           <button 
//             className="vc-control-btn vc-end-btn" 
//             onClick={handleEndMeeting}
//             disabled={isEnding}
//           >
//             <MdCallEnd />
//           </button>
//         </div>
        
//         <div className="vc-controls-right">
//           <button 
//             className="vc-control-btn vc-minimize-btn" 
//             onClick={onToggleMinimize}
//           >
//             <FaWindowMinimize />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// });

// export default VideoCall;